import { useEffect, useMemo, useRef } from 'react';

import type { MapInterface, MapProps, map as mapFactory } from '../utils/map';

type MapFactory = typeof mapFactory;
type MapInstance = MapInterface;

export interface UseMapControllerOptions extends Omit<
  MapProps,
  'mapContainer'
> {
  /** Notify parent when map instance is ready */
  onReady?: (instance: MapInstance) => void;
  /** Notify parent when map instance is disposed */
  onDispose?: () => void;
  /** Enable Backspace/Delete keyboard events for deleting selected features */
  enableDeleteKeys?: boolean;
  /** Access token for Mapbox API */
  accessToken: string;
}

export function useMapController(
  {
    onReady,
    onDispose,
    enableDeleteKeys = true,
    accessToken = '',
    ...mapInitProps
  }: UseMapControllerOptions = { accessToken: '' }
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<MapInstance | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        const { map } = (await import('../utils/map')) as { map: MapFactory };
        if (cancelled || !containerRef.current || mapInstanceRef.current) {
          return;
        }

        mapInstanceRef.current = map({
          mapContainer: containerRef.current,
          ...mapInitProps
        });

        await mapInstanceRef.current.init(accessToken as string);

        if (cancelled) {
          // If unmounted during init, dispose immediately
          mapInstanceRef.current.dispose();
          mapInstanceRef.current = null;
          onDispose?.();
          return;
        }

        onReady?.(mapInstanceRef.current);
      } catch (error) {
        console.error(
          '[useMapController] Error initializing map: init failed',
          error
        );
      }
    })();

    return () => {
      cancelled = true;

      try {
        mapInstanceRef.current?.dispose();
      } catch (error) {
        console.error(
          '[useMapController] Error disposing map: dispose failed',
          error
        );
      } finally {
        mapInstanceRef.current = null;
        onDispose?.();
      }
    };
    // Intentionally to avoid re-init loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => {
    const deleteSelected = () => {
      if (!mapInstanceRef.current) return;
      const id = mapInstanceRef.current.getCurrentSelectedFeatureId();
      if (id != null) mapInstanceRef.current.deleteFeature(id);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = e => {
      if (!enableDeleteKeys) return;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        deleteSelected();
      }
    };

    return { containerRef, onKeyDown, deleteSelected };
  }, [enableDeleteKeys]);
}
